import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { Alert, Box, Button, Typography } from "@mui/material";
import {
  ArrowBackRounded,
  ErrorOutlineRounded,
  ReplayRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingAnswersPayload,
  DocumentCraftingRun,
  DocumentCraftingRevisionStatus,
  DocumentCraftingRunKind,
  DocumentCraftingRunStatus,
  isRunTerminal,
} from "../../../Models/documentCrafting";
import {
  useAnswerDocumentCraftingRun,
  useCancelDocumentCraftingRun,
  useDocumentCraftingDocument,
  useDocumentCraftingRun,
  useRetryDocumentCraftingRun,
} from "../../../Query/useDocumentCrafting";
import ConfirmDialog from "../Common/ConfirmDialog";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
} from "../Common/DocumentCraftingUi";
import { documentCraftingPaths } from "../Common/documentCraftingNavigation";
// `?revision=draft` opens the caller's draft on the detail page.
import { DRAFT_PARAM } from "../Detail/detailModel";
import { useDocumentCraftingEnumLabels } from "../Common/useDocumentCraftingEnumLabels";
import {
  documentCraftingErrorText,
  getDocumentCraftingError,
} from "../../../Api/documentCrafting";
import QuestionsStep from "./QuestionsStep";
import RunProgress from "./RunProgress";

const TITLE_KEYS: Record<DocumentCraftingRunKind, string> = {
  [DocumentCraftingRunKind.CREATE]: "run.titles.create",
  [DocumentCraftingRunKind.REVISE]: "run.titles.revise",
  [DocumentCraftingRunKind.REFINE]: "run.titles.refine",
  [DocumentCraftingRunKind.UPLOAD]: "run.titles.upload",
  [DocumentCraftingRunKind.TRANSLATE]: "run.titles.translate",
};

/** Seconds since the run started, counting on locally between two polls. */
const useElapsedSeconds = (
  run: DocumentCraftingRun | undefined,
  receivedAt: number,
  running: boolean,
) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);
  if (!run) return 0;
  const sinceReceived = running ? Math.max(0, Math.floor((now - receivedAt) / 1000)) : 0;
  return run.elapsed_seconds + sinceReceived;
};

const RunFailure: React.FC<{
  run: DocumentCraftingRun;
  onRetry: () => void;
  retryPending: boolean;
  documentAvailable: boolean;
}> = ({ run, onRetry, retryPending, documentAvailable }) => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const cancelled = run.status === DocumentCraftingRunStatus.CANCELLED;
  const reason = cancelled
    ? t("run.failure.cancelledText")
    : labels.errorCode(run.error_code, run.error);
  const showServerText = !cancelled && !!run.error && run.error !== reason;

  return (
    <Panel>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <ErrorOutlineRounded sx={{ fontSize: 32, color: cancelled ? color.gray400 : color.red600 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: color.gray900 }}>
            {t(cancelled ? "run.failure.cancelledTitle" : "run.failure.title")}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: color.gray700 }}>{reason}</Typography>
          {showServerText && (
            <Typography sx={{ mt: 1, fontSize: 12, color: color.gray500, overflowWrap: "anywhere" }}>
              {t("run.failure.details", { text: run.error })}
            </Typography>
          )}
          <Typography sx={{ mt: 1, fontSize: 13, color: color.gray500 }}>
            {t("run.failure.retryHint")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<ReplayRounded />}
              onClick={onRetry}
              disabled={retryPending}
            >
              {t("run.failure.retry")}
            </Button>
            <Button
              color="secondary"
              variant="outlined"
              startIcon={<ArrowBackRounded />}
              onClick={() =>
                navigate(
                  documentAvailable
                    ? documentCraftingPaths.document(run.document_id)
                    : documentCraftingPaths.list,
                )
              }
            >
              {documentAvailable ? t("nav.backToDocument") : t("nav.backToList")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Panel>
  );
};

/**
 * `/document-crafting/runs/:runId`: a run of any kind while it works. Shows
 * the questions when the run waits for answers, the progress while it
 * generates, the reason and Retry when it failed, and opens the document as
 * soon as it is ready.
 */
const RunView: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  const navigate = useNavigate();
  const labels = useDocumentCraftingEnumLabels();
  const params = useParams();
  const runId = Number(params.runId) || undefined;
  const runQuery = useDocumentCraftingRun(runId);
  const run = runQuery.data;
  const documentQuery = useDocumentCraftingDocument(run?.document_id);
  const answer = useAnswerDocumentCraftingRun();
  const cancel = useCancelDocumentCraftingRun();
  const retry = useRetryDocumentCraftingRun();
  const [cancelOpen, setCancelOpen] = useState(false);
  // A rejected answers submit (400 `invalid_answers`): per question and overall.
  const [answerErrors, setAnswerErrors] = useState<Record<string, string>>({});
  const [answerErrorMessage, setAnswerErrorMessage] = useState<string | null>(null);

  const running = !!run && !isRunTerminal(run.status);
  const elapsed = useElapsedSeconds(run, runQuery.dataUpdatedAt, running);

  if (!runId) return <Navigate to={documentCraftingPaths.list} replace />;
  if (runQuery.isLoading) return <LoadingState />;
  if (!run) {
    const status = isAxiosError(runQuery.error) ? runQuery.error.response?.status : undefined;
    return (
      <>
        <PageHeader title={t("run.titles.generic")} />
        <ErrorState
          text={status === 404 || status === 403 ? t("run.notFound") : undefined}
          onRetry={status === 404 || status === 403 ? undefined : () => runQuery.refetch()}
        />
      </>
    );
  }

  if (run.status === DocumentCraftingRunStatus.READY) {
    // A new revision or refinement ends as the caller's draft; everything
    // else as a saved revision.
    const draft = run.result?.revision_status === DocumentCraftingRevisionStatus.DRAFT;
    return (
      <Navigate
        to={
          draft
            ? `${documentCraftingPaths.document(run.document_id)}?revision=${DRAFT_PARAM}`
            : documentCraftingPaths.document(run.document_id, run.result?.revision_number ?? null)
        }
        replace
      />
    );
  }

  const documentTitle = documentQuery.data?.title;
  const subtitle = [documentTitle, documentQuery.data ? labels.type(documentQuery.data.type_key) : null]
    .filter(Boolean)
    .join(" · ");

  const submitAnswers = async (payload: DocumentCraftingAnswersPayload) => {
    setAnswerErrors({});
    setAnswerErrorMessage(null);
    try {
      await answer.mutateAsync({ runId: run.id, payload });
    } catch (error) {
      // Other errors were already announced; `invalid_answers` is shown on
      // the questions it concerns.
      const body = getDocumentCraftingError(error);
      if (body?.code !== "invalid_answers") return;
      const perQuestion: Record<string, string> = {};
      const raw = body.errors;
      if (raw && typeof raw === "object") {
        Object.keys(raw).forEach((id) => {
          const message = (raw as Record<string, unknown>)[id];
          perQuestion[id] = typeof message === "string" ? message : String(message);
        });
      }
      setAnswerErrors(perQuestion);
      setAnswerErrorMessage(
        Object.keys(perQuestion).length
          ? t("run.questions.rejected")
          : (documentCraftingErrorText(error) ?? t("run.questions.rejected")),
      );
    }
  };

  const doRetry = async () => {
    try {
      const result = await retry.mutateAsync(run.id);
      navigate(documentCraftingPaths.run(result.run_id), { replace: true });
    } catch (error) {
      // The request already showed what went wrong; follow the run that won
      // (e.g. a retry started from another tab).
      const body = getDocumentCraftingError(error);
      if (
        (body?.code === "run_active" || body?.code === "run_superseded") &&
        typeof body.run_id === "number"
      ) {
        navigate(documentCraftingPaths.run(body.run_id), { replace: true });
      } else if (body?.code === "translation_ready") {
        navigate(documentCraftingPaths.document(run.document_id));
      }
    }
  };

  const doCancel = async () => {
    try {
      await cancel.mutateAsync(run.id);
    } catch {
      // The request already showed what went wrong.
    }
    setCancelOpen(false);
  };

  const failed =
    run.status === DocumentCraftingRunStatus.FAILED || run.status === DocumentCraftingRunStatus.CANCELLED;
  const awaiting = run.status === DocumentCraftingRunStatus.AWAITING_ANSWERS;

  return (
    <>
      <PageHeader
        title={t(TITLE_KEYS[run.kind] ?? "run.titles.generic")}
        subtitle={subtitle || undefined}
        actions={
          <Button
            color="secondary"
            startIcon={<ArrowBackRounded />}
            onClick={() =>
              navigate(
                documentQuery.data?.latest_revision
                  ? documentCraftingPaths.document(run.document_id)
                  : documentCraftingPaths.list,
              )
            }
          >
            {documentQuery.data?.latest_revision ? t("nav.backToDocument") : t("nav.backToList")}
          </Button>
        }
      />

      {runQuery.isError && !failed && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => runQuery.refetch()}>
              {t("common.retry")}
            </Button>
          }
        >
          {t("run.pollError")}
        </Alert>
      )}

      {failed ? (
        <RunFailure
          run={run}
          onRetry={doRetry}
          retryPending={retry.isPending}
          documentAvailable={!!documentQuery.data?.latest_revision}
        />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: awaiting ? "minmax(0, 1fr) 340px" : "minmax(0, 720px)" },
            justifyContent: awaiting ? undefined : "center",
            gap: 3,
            alignItems: "start",
          }}
        >
          {awaiting && (
            <QuestionsStep
              key={run.id}
              run={run}
              onSubmit={submitAnswers}
              pending={answer.isPending}
              errors={answerErrors}
              errorMessage={answerErrorMessage}
              onClearError={(questionId) => {
                const next = { ...answerErrors };
                delete next[questionId];
                setAnswerErrors(next);
                if (!Object.keys(next).length) setAnswerErrorMessage(null);
              }}
            />
          )}
          <RunProgress
            run={run}
            elapsedSeconds={elapsed}
            onCancel={() => setCancelOpen(true)}
            cancelPending={cancel.isPending}
          />
        </Box>
      )}

      <ConfirmDialog
        open={cancelOpen}
        title={t("run.cancelDialog.title")}
        text={t(
          run.kind === DocumentCraftingRunKind.CREATE ? "run.cancelDialog.textCreate" : "run.cancelDialog.text",
        )}
        confirmLabel={t("run.cancelDialog.confirm")}
        danger
        pending={cancel.isPending}
        onConfirm={doCancel}
        onClose={() => setCancelOpen(false)}
      />
    </>
  );
};

export default RunView;
